import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { TEST_CASES } from '../data/calcTestCases';
import './CalcTestRunner.css';

const CATEGORIES = [...new Set(TEST_CASES.map(t => t.category))];

function runAllTests() {
  return TEST_CASES.map(tc => {
    try {
      const result = tc.run();
      return { id: tc.id, name: tc.name, category: tc.category, ...result };
    } catch (err) {
      return {
        id: tc.id, name: tc.name, category: tc.category,
        pass: false,
        message: `Threw: ${err.message}`,
      };
    }
  });
}

export default function CalcTestRunner() {
  const [results, setResults]       = useState(null);   // null = not run yet
  const [running, setRunning]       = useState(false);
  const [filterCat, setFilterCat]   = useState('All');
  const [showFailOnly, setShowFailOnly] = useState(false);
  const [collapsed, setCollapsed]   = useState({});

  const run = useCallback(() => {
    setRunning(true);
    // Defer to next tick so the UI can re-render the spinner first
    setTimeout(() => {
      const r = runAllTests();
      setResults(r);
      setRunning(false);
      // Auto-expand categories that have failures
      const newCollapsed = {};
      CATEGORIES.forEach(cat => {
        const catResults = r.filter(x => x.category === cat);
        const hasFail = catResults.some(x => !x.pass);
        newCollapsed[cat] = !hasFail; // collapse passing categories by default
      });
      setCollapsed(newCollapsed);
    }, 50);
  }, []);

  // Run automatically on mount
  useEffect(() => { run(); }, [run]);

  const toggleCategory = cat =>
    setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  const filteredResults = useMemo(() => {
    if (!results) return null;
    return results
      .filter(r => filterCat === 'All' || r.category === filterCat)
      .filter(r => !showFailOnly || !r.pass);
  }, [results, filterCat, showFailOnly]);

  const passed   = results ? results.filter(r =>  r.pass).length : 0;
  const failed   = results ? results.filter(r => !r.pass).length : 0;
  const total    = results ? results.length : TEST_CASES.length;
  const allPass  = results && failed === 0;

  // Group filtered results by category
  const byCategory = useMemo(() => {
    if (!filteredResults) return {};
    return filteredResults.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});
  }, [filteredResults]);

  const visibleCategories = CATEGORIES.filter(c =>
    byCategory[c] && byCategory[c].length > 0
  );

  return (
    <div className="calc-test-runner">
      <h1>
        Calc Test Runner
        {running && <span className="running-indicator">⟳</span>}
      </h1>

      {/* Summary bar */}
      <div className={`test-summary ${allPass ? 'all-pass' : 'has-fail'}`}>
        <span className="summary-count">
          {results
            ? <><span className="summary-pass">{passed} passed</span>
                {failed > 0 && <> / <span className="summary-fail">{failed} failed</span></>}
                {' '}/ {total} total</>
            : `${total} tests`}
        </span>
        {allPass && results && <span className="summary-pass">All tests passing ✓</span>}
      </div>

      {/* Controls */}
      <div className="test-controls">
        <button className="btn-run" onClick={run} disabled={running}>
          {running ? 'Running…' : 'Run Tests'}
        </button>

        <select
          className="filter-select"
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
        >
          <option value="All">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label className="filter-toggle">
          <input
            type="checkbox"
            checked={showFailOnly}
            onChange={e => setShowFailOnly(e.target.checked)}
          />
          Failures only
        </label>
      </div>

      {/* Test categories */}
      {!results && !running && (
        <div className="test-empty">Press Run Tests to start.</div>
      )}

      {filteredResults && visibleCategories.length === 0 && (
        <div className="test-empty">No tests match the current filters.</div>
      )}

      {visibleCategories.map(cat => {
        const rows = byCategory[cat];
        const catFailed = rows.filter(r => !r.pass).length;
        const isOpen = !collapsed[cat];

        return (
          <div key={cat} className="test-category">
            <div
              className="category-header"
              onClick={() => toggleCategory(cat)}
            >
              <span className="category-title">{cat}</span>
              <span className={`category-badge ${catFailed ? 'has-fail' : 'all-pass'}`}>
                {catFailed
                  ? `${catFailed} failing`
                  : `${rows.length} / ${rows.length} pass`}
              </span>
              <span className={`category-chevron ${isOpen ? 'open' : ''}`}>▶</span>
            </div>

            {isOpen && (
              <div className="test-rows">
                {rows.map(r => (
                  <div key={r.id} className={`test-row ${r.pass ? 'pass' : 'fail'}`}>
                    <span className="test-icon">{r.pass ? '✓' : '✗'}</span>
                    <div className="test-body">
                      <span className="test-name">{r.name}</span>
                      {r.message && (
                        <span className="test-message">{r.message}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
